import React, { useState, useEffect } from "react";
import { TableRow, TableCell } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import { useExpenses } from "./hooks/useExpenses";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../Redux/Actions/SnackbarActions/SnackbarActions";
import { connect } from "react-redux";
import { faTrash, faEye, faEdit, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import axios from "axios";
import config from "../../../Helpers/config.json";
import { v4 as uuidv4 } from "uuid";
import { formatAmount, formatDigits } from "../../../Utils/formatAmount";


import { EnhancedTableHead, CustomPagination, MuiPaper, EnhancedTableToolbar } from "../../../Utils/Tables/CustomTable";
import { CustomTableCell } from "../../../Utils/Tables/StickyHeaderTable";
import { LoadingGif } from "../../../Utils/Loader";
import { formatDate } from "../../../Utils/ConvertDate";
import TextField from "../../../Utils/FormInputs/TextField";
import CustomDialog from "../../../Utils/Dialog/CustomDialog";
import { ExpenseModal } from "./ExpenseModal";

const headData = [
  { id: "date", label: "Date" },
  { id: "vendor", label: "Vendor Name" },
  { id: "category", label: "Category" },
  { id: "details", label: "Details" },
  { id: "payment", label: "Payment" },
  { id: "amount", label: "Amount" },
  { id: "action", label: "Action" },
];

const Expenses = ({ actions, snackbars, match: { params } }) => {
  const { component } = params;
  const {
    expenses,
    isLoading,
    isSuccess,
    activePage,
    total_count,
    handleChangeSearch,
    searchValue,
    handleChangeStartDate,
    handleChangeEndDate,
    startDate,
    endDate,
    handleDeleteExpense,
    refresbdata,
  } = useExpenses(actions, component);

  const [openExpense, setOpenExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  
  // -------------------- Inline Notification --------------------
  const [pageNotification, setPageNotification] = useState(null); // { message: "", type: "success"|"error" }

  const showPageNotification = (message, type = "success") => {
    setPageNotification({ message, type });
    setTimeout(() => setPageNotification(null), 4000); // hide after 4s
  };

  const isActive = component === "active";

  const refreshExpenses = async () => {
    try {
      await handleChangeSearch(""); // trigger fetch
    } catch (err) {
      console.error("Failed to refresh expenses", err);
    }
  };

  // Fetch payment accounts
  useEffect(() => {
    const fetchPaymentAccounts = async () => {
      try {
        const res = await axios.get(`${config.epharmUrl}/accounting/get_cash_and_bank_accounts`);
        const dt = res.data ?? [];
        setPaymentAccounts(dt.map((i) => ({ label: i.head_name, value: i.id })));
      } catch (err) {
        console.error("Error fetching payment accounts:", err);
        setPaymentAccounts([]);
      }
    };
    fetchPaymentAccounts();
  }, []);

  // Open modal for new expense
  const handleOpenExpense = () => {
    setEditingExpense(null);
    setOpenExpense(true);
  };

  // Open modal for editing expense
  const handleEditExpense = async (voucherId) => {
    try {
      const res = await axios.get(`${config.epharmUrl}/accounting/retrive_expenses_voucher?voucher_id=${voucherId}`);
      const data = res.data;

      const formattedExpense = {
        id: data.id,
        expense_date: new Date(data.Date).toISOString().split("T")[0],
        details: data.details,
        vendor: { label: data.vendor_name.vendor_name, value: data.vendor_id },
        vendor_name: data.vendor_name.vendor_name,
        vendor_id: data.vendor_id,
        category: null,
        payment: data.payment_account_id,
        payment_account_id: data.payment_account_id,
        expense_info: (data.expense_details || []).map((item) => ({
          uuid_id: uuidv4(),
          account_name: { label: item.expense, value: item.expense_id },
          amount: item.amount,
          memo: item.memo,
          expense_account_id: item.expense_id,
        })),
      };

      setEditingExpense(formattedExpense);
      setOpenExpense(true);
    } catch (err) {
      console.error("Failed to fetch expense for editing", err);
    }
  };

  const handleCloseExpense = () => {
    setOpenExpense(false);
    setEditingExpense(null);
  };

  return (
    <div className="journals">
      {/* -------------------- Page Notification Banner -------------------- */}
      {pageNotification && (
        <div style={{
          padding: "0.5rem 1rem",
          backgroundColor: pageNotification.type === "error" ? "#e74c3c" : "#2ecc71",
          color: "#fff",
          fontWeight: "bold",
          textAlign: "center",
          marginBottom: "1rem",
          borderRadius: "4px",
        }}>
          {pageNotification.message}
        </div>
      )}

      {/* Top Controls */}
      <MuiPaper style={{ marginBottom: "1rem" }}>
        <EnhancedTableToolbar title="">
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
            {isActive && (
              <button
                className="btn btn-sm pharmacy-info-btn"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: "bold" }}
                onClick={handleOpenExpense}
              >
                <FontAwesomeIcon icon={faPlus} /> Record Expense
              </button>
            )}
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
              <TextField type="text" placeholder="Search..." value={searchValue} onChange={handleChangeSearch} style={{ minWidth: 200 }} />
              <TextField type="date" label="Start Date" value={startDate} onChange={(e) => handleChangeStartDate(e.target.value)} style={{ minWidth: 160 }} />
              <TextField type="date" label="End Date" value={endDate} onChange={(e) => handleChangeEndDate(e.target.value)} style={{ minWidth: 160 }} />
            </div>
          </div>
        </EnhancedTableToolbar>
      </MuiPaper>

      {/* Expense Modal */}
      <CustomDialog open={openExpense} handleClose={handleCloseExpense} title={editingExpense ? "Edit Expense" : "Record Expense"} maxWidth="xl">
        <ExpenseModal
          actions={actions}
          open={openExpense}
          onClose={handleCloseExpense}
          editingExpense={editingExpense}
          onSuccess={(message) => {
            handleCloseExpense();
            refresbdata();
            showPageNotification(message, "success"); // <-- inline banner
          }}
        />
      </CustomDialog>

      {/* Expenses Table */}
      <MuiPaper>
        <table>
          <EnhancedTableHead headCells={headData} />
          {isLoading && (
            <TableRow>
              <TableCell align="center" colSpan={headData.length}>
                <LoadingGif />
              </TableCell>
            </TableRow>
          )}

          {isSuccess && expenses?.length > 0 ? (
            expenses.map((row, index) => (
              <TableRow key={index} hover>
                <CustomTableCell>{formatDate(row.expense_date)}</CustomTableCell>
                <CustomTableCell>{row.vendor_name}</CustomTableCell>
                <CustomTableCell>{row.category}</CustomTableCell>
                <CustomTableCell>{row.details}</CustomTableCell>
                <CustomTableCell>{row.payment}</CustomTableCell>
                <CustomTableCell>{formatDigits(row.total_amount)}</CustomTableCell>
                <CustomTableCell style={{ display: "flex", gap: "0.25rem" }}>
                  <Tooltip title="View Receipt">
                    <Link to={`/expense-reciept/${row.voucher_id}`}>
                      <button className="btn btn-sm pharmacy-btn icon-button-holder"><FontAwesomeIcon icon={faEye} /></button>
                    </Link>
                  </Tooltip>

                  <Tooltip title="Edit Expense">
                    <button className="btn btn-sm pharmacy-info-btn icon-button-holder" onClick={() => handleEditExpense(row.voucher_id)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                  </Tooltip>

                  <Tooltip title="Delete Expense">
                    <button className="btn btn-sm pharmacy-danger-btn icon-button-holder" onClick={() => handleDeleteExpense(row)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </Tooltip>
                </CustomTableCell>
              </TableRow>
            ))
          ) : !isLoading ? (
            <TableRow>
              <TableCell align="center" colSpan={headData.length}>No records found</TableCell>
            </TableRow>
          ) : null}

          <CustomPagination data={expenses} records={10} activePage={activePage} total_count={total_count} handleChangeNextPage={() => {}} />
        </table>
      </MuiPaper>
    </div>
  );
};

function mapStateToProps(state) {
  return { snackbars: state.snackbars };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Expenses);
